import React from 'react'

import { makeStyles, useTheme, useMediaQuery, Grid, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme, GridSize } from '@paintscout/ui'
import type { PresentationBlockProps } from '../../'
import compareBlockProps from '../../util/compareBlockProps'
import { InlineEditableHtml, InlineEditableMedia, InlineEditableText } from '../../inlineEditable'
import { usePresentation } from '../../../context'

type GridAlignment = 'flex-start' | 'center' | 'flex-end'

interface RowWidths {
  imageWidth: GridSize
  contentWidth: GridSize
  mobileImageWidth: GridSize
  mobileContentWidth: GridSize
}

interface ServiceRow {
  title?: string
  content?: string
  image?: any
}

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: {},
      content: {},
      image: {
        '& img': {
          borderRadius: theme.spacing(0.5)
        }
      },
      title: {}
    }
  },
  { name: 'ServicesBlock' }
)

interface ServiceRowProps {
  row: ServiceRow
  index: number
  classes: Record<string, string>
  handleEditField: (field: string, content: any) => void
  isEditable: boolean
  isSettings: boolean
  presentationLibrary: any
  presetColors: string[]
  responsiveSpacing: 3 | 6
  blockPrefix: string
  verticalAlignment: GridAlignment
  widths: RowWidths
}

// module-scope so the component type is stable across renders — declaring it inside
// ServicesBlock would remount the row (and drop inline-editor focus) on every render
function ServiceRowItem(props: ServiceRowProps) {
  const {
    row,
    index,
    classes,
    handleEditField,
    isEditable,
    isSettings,
    presentationLibrary,
    presetColors,
    responsiveSpacing,
    blockPrefix,
    verticalAlignment,
    widths
  } = props

  return (
    <Grid container spacing={responsiveSpacing} alignItems={verticalAlignment}>
      {(row.image || isEditable) && (
        <Grid item xs={widths.mobileImageWidth} md={widths.imageWidth}>
          <InlineEditableMedia
            classes={{ root: classes.image }}
            mediaOptionsProps={{
              blockPrefix: blockPrefix,
              fieldSuffix: `rows[${index}].image`
            }}
            presentationLibrary={presentationLibrary}
            file={row.image}
            handleEdit={(file) => handleEditField(`fields.rows[${index}].image`, file)}
            isEditable={isEditable}
            isSettings={isSettings}
            placeholderButtonText={'Add Image'}
          />
        </Grid>
      )}

      <Grid item xs={widths.mobileContentWidth} md={widths.contentWidth}>
        <Grid container spacing={3}>
          {(row.title || isEditable) && (
            <Grid item md={12}>
              {isEditable ? (
                <InlineEditableText
                  variant={'h3'}
                  isSubmitOnEnter={true}
                  handleEdit={(content) => handleEditField(`fields.rows[${index}].title`, content)}
                  placeholderButtonText={'Add Title'}
                  classes={{ root: classes.title }}
                >
                  {row.title}
                </InlineEditableText>
              ) : (
                <Typography variant={'h3'}>{row.title}</Typography>
              )}
            </Grid>
          )}
          <Grid item md={12}>
            <InlineEditableHtml
              isEditable={isEditable}
              isSettings={isSettings}
              handleEdit={(content) => handleEditField(`fields.rows[${index}].content`, content)}
              content={row.content}
              placeholderButtonText={'Add Text'}
              classes={{ root: classes.content }}
              presetColors={presetColors}
              fieldKey={`${blockPrefix}.fields.rows[${index}].content`}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

function getWidths(width?: string): RowWidths {
  switch (width) {
    case 'small':
      return { imageWidth: 2, contentWidth: 10, mobileImageWidth: 3, mobileContentWidth: 9 }
    case 'medium':
      return { imageWidth: 4, contentWidth: 8, mobileImageWidth: 4, mobileContentWidth: 8 }
    case 'large':
      return { imageWidth: 6, contentWidth: 6, mobileImageWidth: 4, mobileContentWidth: 8 }
    default:
      return { imageWidth: 3, contentWidth: 9, mobileImageWidth: 3, mobileContentWidth: 9 }
  }
}

function getAlignment(alignment?: string): GridAlignment {
  switch (alignment) {
    case 'top':
      return 'flex-start'
    case 'bottom':
      return 'flex-end'
    default:
      return 'center'
  }
}

function ServicesBlock(props: PresentationBlockProps) {
  const classes = useStyles(props)
  const { isEditable, isSettings, presetColors, block, blockPrefix, setFieldValue, submitForm, WrapperComponent } =
    props
  const { presentationLibrary } = usePresentation()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const responsiveSpacing = isMobile ? 3 : 6

  const { fields, title } = block
  const rows: ServiceRow[] = fields?.rows ?? []

  const handleEditField = (field: string, content: any) => {
    setFieldValue(`${blockPrefix}.${field}`, content)
    submitForm()
  }

  const widths = getWidths(fields?.width)
  const verticalAlignment = getAlignment(fields?.alignment)

  return (
    <WrapperComponent {...props}>
      <Grid container spacing={responsiveSpacing}>
        {(title || isEditable) && (
          <Grid item xs={12} md={12}>
            {isEditable ? (
              <InlineEditableText
                variant={'h2'}
                isSubmitOnEnter={true}
                handleEdit={(content) => handleEditField('title', content)}
                placeholderButtonText={'Add Title'}
                classes={{ root: classes.title }}
              >
                {title}
              </InlineEditableText>
            ) : (
              <Typography variant={'h2'}>{title}</Typography>
            )}
          </Grid>
        )}
        {rows.map((row, index) => (
          <Grid item xs={12} md={12} key={index}>
            <ServiceRowItem
              row={row}
              index={index}
              classes={classes}
              handleEditField={handleEditField}
              isEditable={isEditable}
              isSettings={isSettings}
              presentationLibrary={presentationLibrary}
              presetColors={presetColors}
              responsiveSpacing={responsiveSpacing}
              blockPrefix={blockPrefix}
              verticalAlignment={verticalAlignment}
              widths={widths}
            />
          </Grid>
        ))}
      </Grid>
    </WrapperComponent>
  )
}

export default React.memo(ServicesBlock, (prevProps, nextProps) => {
  return compareBlockProps(prevProps, nextProps)
})
